import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import styled from 'styled-components'
import axios from 'axios'
import { demoAPI } from '@src/api'
import { useAuth } from '@src/context/AuthContext'
import { useTurnstile } from '@src/hooks/useTurnstile'
import type { User } from '@src/types'
import {
  WshModal,
  WshModalBackdrop,
  WshModalHeader,
  WshModalBody,
  WshModalFooter,
  WshFormGroup,
  WshFormLabel,
  WshFormInput,
  WshFieldError,
  WshSubmitBtn,
} from './WshModalShell'
import { wshColors } from './wshComponents'

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'wsh_demo_prefill'

interface DemoPrefill {
  email: string
  company: string
}

// ─── Schema ───────────────────────────────────────────────────────────────────

const demoSchema = z.object({
  email: z.string().min(1, 'Email is required.').email('Please enter a valid email address.'),
  company: z.string().min(1, 'Company name is required.').max(100, 'Company name is too long.'),
  role: z.enum(['admin', 'customer'], { error: 'Please choose a demo type.' }),
})

type DemoFormData = z.infer<typeof demoSchema>

// ─── Role selector styled components ─────────────────────────────────────────

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`

const RoleCard = styled.button<{ $active: boolean }>`
  border: 2px solid ${p => p.$active ? wshColors.primary : '#d1d5db'};
  border-radius: 0.5rem;
  background: ${p => p.$active ? `${wshColors.primary}0f` : 'white'};
  padding: 0.85rem 0.75rem;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.15s, background 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  text-align: center;

  &:hover {
    border-color: ${wshColors.primary};
    background: ${wshColors.primary}0a;
  }
`

const RoleIcon = styled.i<{ $active: boolean }>`
  font-size: 1.5rem;
  color: ${p => p.$active ? wshColors.primary : '#6b7280'};
`

const RoleTitle = styled.span<{ $active: boolean }>`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${p => p.$active ? wshColors.primary : '#374151'};
`

const RoleDesc = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  line-height: 1.3;
`

const ErrorText = styled.div`
  font-size: 0.78rem;
  color: #dc2626;
  margin-top: 0.35rem;
`

const ApiErrorText = styled.div`
  font-size: 0.83rem;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
  margin-bottom: 0.75rem;
`

// ─── Component ────────────────────────────────────────────────────────────────

const WshDemoModal: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuth()
  const [apiError, setApiError] = useState('')

  const paramRole = searchParams.get('demo') as 'admin' | 'customer' | null
  const validRole = paramRole === 'admin' || paramRole === 'customer' ? paramRole : null

  const savedPrefill = (): DemoPrefill => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : { email: '', company: '' }
    } catch {
      return { email: '', company: '' }
    }
  }

  const prefill = savedPrefill()

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<DemoFormData>({
    resolver: zodResolver(demoSchema),
    defaultValues: {
      email: prefill.email,
      company: prefill.company,
      role: validRole ?? undefined,
    },
  })

  const selectedRole = watch('role')

  const selectRole = (role: 'admin' | 'customer') => {
    setValue('role', role, { shouldValidate: true })
  }

  const { turnstileRef, getToken, reset: resetTurnstile } = useTurnstile(true)

  useEffect(() => {
    if (validRole) setValue('role', validRole)
  }, [validRole, setValue])

  const onSubmit = async (data: DemoFormData) => {
    setApiError('')

    const turnstileToken = getToken()
    if (!turnstileToken) {
      setApiError('Please complete the security check.')
      return
    }

    try {
      const { demo_token } = await demoAPI.requestAccess({
        email: data.email,
        company: data.company,
        role: data.role,
        turnstileToken,
      })

      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: data.email, company: data.company }))

      const loginResponse = await demoAPI.login({ demo_token, role: data.role })
      login(loginResponse.user as User, loginResponse.sessionExpiresAt)

      navigate(data.role === 'admin' ? '/admin-portal' : '/products', { replace: true })

    } catch (error: unknown) {
      resetTurnstile()
      if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.error || 'Something went wrong. Please try again.')
      } else {
        setApiError('Something went wrong. Please try again.')
      }
    }
  }

  return (
    <>
      <WshModalBackdrop />
      <WshModal show centered backdrop={false} keyboard={false} onHide={() => {}}>
        <WshModalHeader tagline="Interactive Demo" />
        <WshModalBody>
          <form id="demo-access-form" onSubmit={handleSubmit(onSubmit)}>

            {apiError && <ApiErrorText>{apiError}</ApiErrorText>}

            <WshFormGroup>
              <WshFormLabel htmlFor="demo-email">Work Email</WshFormLabel>
              <WshFormInput
                id="demo-email"
                type="email"
                placeholder="you@company.com"
                maxLength={120}
                autoFocus={!prefill.email}
                {...register('email')}
              />
              {errors.email && <WshFieldError>{errors.email.message}</WshFieldError>}
            </WshFormGroup>

            <WshFormGroup>
              <WshFormLabel htmlFor="demo-company">Company</WshFormLabel>
              <WshFormInput
                id="demo-company"
                type="text"
                placeholder="Your company name"
                maxLength={100}
                {...register('company')}
              />
              {errors.company && <WshFieldError>{errors.company.message}</WshFieldError>}
            </WshFormGroup>

            <WshFormGroup>
              <WshFormLabel>I want to demo as...</WshFormLabel>
              <RoleGrid>
                <RoleCard
                  type="button"
                  $active={selectedRole === 'customer'}
                  onClick={() => selectRole('customer')}
                >
                  <RoleIcon className="bi bi-person-circle" $active={selectedRole === 'customer'} />
                  <RoleTitle $active={selectedRole === 'customer'}>Customer</RoleTitle>
                  <RoleDesc>Browse products, place orders, track deliveries</RoleDesc>
                </RoleCard>
                <RoleCard
                  type="button"
                  $active={selectedRole === 'admin'}
                  onClick={() => selectRole('admin')}
                >
                  <RoleIcon className="bi bi-person-workspace" $active={selectedRole === 'admin'} />
                  <RoleTitle $active={selectedRole === 'admin'}>Admin</RoleTitle>
                  <RoleDesc>Manage catalog, customers, orders, and settings</RoleDesc>
                </RoleCard>
              </RoleGrid>
              {errors.role && <ErrorText>{errors.role.message}</ErrorText>}
            </WshFormGroup>

            <div style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }}>
              <div ref={turnstileRef} />
            </div>

          </form>
        </WshModalBody>
        <WshModalFooter>
          <WshSubmitBtn type="submit" form="demo-access-form" disabled={isSubmitting} style={{ width: '100%', justifyContent: 'center' }}>
            {isSubmitting
              ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Starting demo...</>
              : <><i className="bi bi-play-circle" />Start Demo</>}
          </WshSubmitBtn>
        </WshModalFooter>
      </WshModal>
    </>
  )
}

export default WshDemoModal
